/**
 * How long a home has been listed, as a line you can print — or null when we do
 * not know.
 *
 * Two bugs lived in the two places this replaces, and they were opposite halves
 * of the same mistake: `daysOnMarket === 0` means "listed today", and both
 * callers read it as "no data".
 *
 * - `ListingCard` guarded on `daysOnMarket > 0`, so the newest homes on a results
 *   page were the only ones with no "listed" line at all.
 * - `propertyFacts` used `listing.daysOnMarket || daysSince(listDate, localTodayISO())`,
 *   so a zero fell through to a CLIENT-SIDE recomputation in the browser's own
 *   timezone. The API computes this in market time from a Postgres DATE; a buyer
 *   in New York at 9pm PT is already on tomorrow's date locally and counts one day
 *   more. That is a real source of the same home reading differently on the search
 *   page and the detail page.
 *
 * So: the server figure is authoritative and is never recomputed here. The list
 * date is used only to tell "listed today" apart from "we were not told" — which
 * is the same discriminator the Go side uses (`l.DaysOnMarket > 0 || l.ListDate != nil`).
 */
export function listedAgoLabel(
  daysOnMarket: number | undefined,
  listDate: string | undefined
): string | null {
  if (typeof daysOnMarket !== 'number' || !Number.isFinite(daysOnMarket)) return null;
  // A negative figure is a bug upstream, not a listing from the future. Printing
  // it would put "Listed -2 days ago" under a real address.
  if (daysOnMarket < 0) return null;
  if (daysOnMarket === 0) {
    // Zero with no list date is "unknown", not "today".
    if (!listDate) return null;
    return 'Listed today';
  }
  return `Listed ${daysOnMarket} ${daysOnMarket === 1 ? 'day' : 'days'} ago`;
}
